import { t } from '../../i18n';
import { formatDateTime, formatDuration, type Sla } from './data';

/** El SLA en palabras (lo ve también el solicitante, sin jerga): la frase principal y un detalle. */
export function describeSla(sla: Sla): { main: string; detail: string | null; late: boolean } {
  const target = sla.targetMs !== null ? formatDuration(sla.targetMs) : null;
  const late = sla.state === 'incumplido' || (sla.state === 'en_curso' && (sla.remainingMs ?? 0) < 0);
  switch (sla.state) {
    case 'sin_sla':
      return { main: t('tk.sla_sin_sla'), detail: null, late };
    case 'esperando_respuesta':
      return { main: t('tk.sla_esperando'), detail: target && t('tk.sla_meta', { meta: target }), late };
    case 'en_curso':
      return late
        ? { main: t('tk.sla_vencido', { tiempo: formatDuration(sla.remainingMs!) }), detail: null, late }
        : {
            main: t('tk.sla_vence', { fecha: formatDateTime(sla.dueAt!) }),
            detail: sla.remainingMs !== null ? t('tk.sla_restante', { tiempo: formatDuration(sla.remainingMs), meta: target ?? '—' }) : null,
            late,
          };
    case 'pausado':
      return {
        main: t('tk.sla_en_pausa', { fecha: formatDateTime(sla.pausedSince!) }),
        detail: t('tk.sla_consumido', { tiempo: formatDuration(sla.consumedMs), meta: target ?? '—' }),
        late,
      };
    case 'cumplido':
    case 'incumplido':
      return {
        main: t(sla.state === 'cumplido' ? 'tk.sla_cumplido' : 'tk.sla_incumplido'),
        detail: t('tk.sla_consumido', { tiempo: formatDuration(sla.consumedMs), meta: target ?? '—' }),
        late,
      };
    case 'cancelado':
      return { main: t('tk.sla_cancelado'), detail: null, late };
  }
}

/** Frase corta para listas: "Quedan 2 h", "Vencido hace 1 h", "En pausa"… */
export function shortSla(sla: Sla): string {
  switch (sla.state) {
    case 'en_curso':
      return (sla.remainingMs ?? 0) < 0 ? t('tk.sla_corto_vencido', { tiempo: formatDuration(sla.remainingMs!) }) : t('tk.sla_corto_quedan', { tiempo: formatDuration(sla.remainingMs ?? 0) });
    case 'pausado':
      return t('tk.sla_pausado');
    case 'esperando_respuesta':
      return t('tk.sla_corto_esperando');
    default:
      return t('tk.sla_corto_sin_sla');
  }
}

/** Barra del tiempo consumido: verde, ámbar desde el porcentaje de aviso, roja al vencer. */
export function SlaBar({ sla, warnPercent = 75 }: { sla: Sla; warnPercent?: number }) {
  if (sla.ratio === null || sla.state === 'cancelado') return null;
  const ratio = Math.min(Math.max(sla.ratio, 0), 1);
  const bar = sla.ratio >= 1 ? 'bg-red-600' : sla.ratio * 100 >= warnPercent ? 'bg-amber-500' : 'bg-green-600';
  return (
    <div
      role="progressbar"
      aria-label={t('tk.sla_progreso')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(sla.ratio * 100)}
      className="mt-2 h-2 overflow-hidden rounded bg-slate-100"
    >
      <div className={`h-full ${bar}`} style={{ width: `${ratio * 100}%` }} />
    </div>
  );
}
